import { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { getPortraitStatus } from "~/api/portrait"
import { AnimatedProcessingScreen } from "~/components/AnimatedProcessingScreen"

const MIN_ONBOARDING_MS = 20_000
const POLL_INTERVAL_MS = 2_000

function isPortraitWaitComplete(status: string | null | undefined): boolean {
  if (status === "ready" || status === "failed") return true
  if (status === "generating") return false
  // No dog profile yet, or portrait not queued (signup-before-assessment).
  return true
}

export function PortraitOnboardingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [dogName, setDogName] = useState("your dog")
  const [readyToFinish, setReadyToFinish] = useState(false)
  const [portraitStatus, setPortraitStatus] = useState<string | null | undefined>(undefined)

  const phrases = useMemo(
    () => [
      "Welcome back! Thanks for verifying your email.",
      `Getting everything ready for ${dogName}… Creating your personalised Doggy Dynamo experience.`,
      `Learning what makes ${dogName} unique… Tailoring guidance to their personality.`,
      `Creating something special for ${dogName}…`,
      `Preparing Ask Dynamo… Personalising answers just for ${dogName}.`,
      "Almost there… Adding the finishing touches.",
    ],
    [dogName],
  )

  useEffect(() => {
    const minTimer = setTimeout(() => setReadyToFinish(true), MIN_ONBOARDING_MS)
    return () => clearTimeout(minTimer)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function poll() {
      try {
        const data = await getPortraitStatus()
        if (cancelled) return
        if (data.dog_name) setDogName(data.dog_name)
        setPortraitStatus(data.portrait?.status ?? null)
      } catch {
        // Keep animation running; dashboard will show stock fallback
      }
    }

    poll()
    const interval = setInterval(poll, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    if (!readyToFinish) return
    if (!isPortraitWaitComplete(portraitStatus)) return

    const emailVerified = searchParams.get("email_verified") === "1" ? "1" : undefined
    navigate(
      emailVerified ? "/dashboard?email_verified=1" : "/dashboard",
      { replace: true },
    )
  }, [readyToFinish, portraitStatus, navigate, searchParams])

  return (
    <AnimatedProcessingScreen
      phrases={phrases}
      footerText={`${dogName}'s Doggy Dynamo experience is almost ready.`}
      phraseIntervalMs={3333}
    />
  )
}
